<template lang="pug">
  .input(:class="inputClass" :data-type="type" ref="input")
    component.input__element(
      :is="tagName"
      :class="{ 'visually-hidden': type === 'checkbox' }"
      :type="type"
      :id="id"
      :name="name"
      :placeholder="placeholder"
      :minlength="minLength"
      :pattern="pattern"
      :required="required"
      @focus="onFocus"
      @blur="onBlur"
    )
    label.input__label(
      :class="labelClass"
      :for="id"
    )
      | {{ labelText }}
      slot
</template>

<script>
import Inputmask from 'inputmask/dist/inputmask/inputmask.numeric.extensions'
import autosize from 'autosize'

export default {
  props: {
    tagName: {
      type: String,
      default: `input`
    },
    inputClass: {
      type: String,
      default: ``
    },
    type: {
      type: String,
      default: `text`
    },
    id: {
      type: String,
      default: ``
    },
    name: {
      type: String,
      default: ``
    },
    placeholder: String,
    minLength: Number,
    pattern: String,
    required: {
      type: Boolean,
      default: false
    },
    labelClass: {
      type: String,
      default: ``
    },
    labelText: {
      type: String,
      default: ``
    }
  },
  methods: {
    onFocus (evt) {
      this.$refs.input.classList.add(`active`)
    },
    onBlur (evt) {
      if (!evt.target.value.trim().length) {
        this.$refs.input.classList.remove(`active`)
      }

      if (!evt.target.validity.valid) {
        this.$refs.input.classList.add(`invalid`)
        evt.target.addEventListener(`input`, this.onInput)
      } else {
        this.$refs.input.classList.remove(`invalid`)
        evt.target.removeEventListener(`input`, this.onInput)
      }
    },
    onInput (evt) {
      if (evt.target.checkValidity()) {
        this.$refs.input.classList.remove(`invalid`)
      } else {
        this.$refs.input.classList.add(`invalid`)
      }
    }
  },
  mounted () {
    const telInputs = document.querySelectorAll(`.input__element[type=tel]`)
    const textareas = document.querySelectorAll(`.modal .input textarea`)

    if (telInputs.length) {
      telInputs.forEach((input) => {
        const im = new Inputmask(`+9(999)999-99-99`, {
          showMaskOnHover: false
        })
        im.mask(input)
      })
    }

    if (textareas.length) {
      textareas.forEach((textarea) => autosize(textarea))
    }
  }
}
</script>

<style lang="scss">
  .input {
    position: relative;
    display: flex;

    &.invalid:not([data-type='checkbox']) {
      box-shadow: inset 0 0 0 1px #ad033b;
    }
  }

  .input__element {
    width: 100%;
    min-height: 3.5rem;
    padding: 1.25rem 0.875rem;
    font-size: 0.875rem;
    line-height: 1;
    letter-spacing: 0.01em;
    color: currentColor;
    border: 1px solid rgba($white, 0.2);
    background-color: transparent;
    resize: none;
    transition: $trs;

    &[type='checkbox'] {
      width: auto;
      min-height: auto;
    }

    &:hover {
      border-color: rgba($white, 0.4);
    }

    &:focus {
      border-color: rgba($white, 1);
    }

    .input--dark & {
      color: #60749d;
      border-color: rgba(#c9d0dc, 0.4);

      &:hover {
        border-color: rgba(#c9d0dc, 0.6);
      }

      &:focus {
        border-color: rgba(#c9d0dc, 1);
      }
    }
  }

  .input__label {
    position: absolute;
    top: 1.1rem;
    left: 0.625rem;
    padding: 0.25rem;
    font-size: 0.875rem;
    line-height: 1;
    letter-spacing: 0.01em;
    color: rgba($white, 0.6);
    transition: $trs;
    user-select: none;

    a {
      font-size: 0.875rem;
      line-height: 1;
      letter-spacing: 0.01em;
    }

    .input__element[type='checkbox'] + & {
      position: relative;
      top: 0;
      left: 0;
      min-height: 1.25rem;
      padding-left: 2rem;
      padding-right: 0.5rem;
      line-height: 1.25rem;
      color: rgba($white, 0.3);
      cursor: pointer;

      a {
        padding-bottom: 3px;
        border-bottom: 1px solid rgba($white, 0.3);
        color: rgba($white, 0.3);
        transition: $trs;

        &:hover {
          border-bottom: 1px solid rgba($white, 0.8);
          color: rgba($white, 0.8);
        }
      }
    }

    .input__element:hover + & {
      color: rgba($white, 0.8);
    }

    .input.active .input__element:not([type='checkbox']) + & {
      top: -0.6875rem;
      color: rgba($white, 1);
    }

    .input__element[type='checkbox']:focus + & {
      top: 0;
      color: rgba($white, 0.3);
    }

    .input__element[type='checkbox']:hover + & {
      color: rgba($white, 0.8);
    }

    .input--dark .input__element:not([type='checkbox']) + &,
    .input--dark.active .input__element:not([type='checkbox']) + & {
      color: #60749d;
    }

    .input--dark .input__element:hover + &,
    .input--dark .input__element:focus + & {
      color: #60749d;
    }

    .input--dark .input__element[type='checkbox'] + & {
      color: $black;
    }

    .input--dark .input__element[type='checkbox'] + & a {
      color: $black;
      border-bottom: 1px solid rgba($black, 0.3);
    }

    .input--dark .input__element[type='checkbox']:hover + & {
      color: #000000;
    }

    .input--dark .input__element[type='checkbox'] + & a:hover {
      border-bottom: 1px solid rgba($black, 1);
    }
  }

  .input__label-checkbox {
    position: absolute;
    top: 2px;
    left: 0;
    display: flex;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 2px;
    background-repeat: no-repeat;
    background-position: center;
    transition: $trs;

    .input--dark & {
      border: 1px solid $l-blue;
    }

    svg {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 60%;
      height: 45%;
      opacity: 0;
      transition: $trs;
    }

    .input__element:checked + .input__label & {
      background-color: $l-blue;

      svg {
        opacity: 1;
      }
    }
  }
</style>
